import { Navigate, Outlet, Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  FileText,
  Home,
  Newspaper,
  Calendar,
  Image,
  Settings,
  LogOut,
  Menu,
  ClipboardList,
  Briefcase,
  Users,
  History,
  ChevronDown,
  MessageSquareQuote,
  GalleryHorizontal,
  Bot,
  CalendarDays,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getAdminToken } from '../../utils/adminAuth'
import { ADMIN_FORM_MODULES, ADMIN_NAV_MODULES, ADMIN_RECRUITMENT_MODULES, ADMIN_STORY_CARD_MODULES } from '../../constants/adminModules'
import { ADMIN_DASHBOARD_PATH, ADMIN_LOGIN_PATH, adminPath } from '../../constants/adminRoutes'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import AdminTopBar from '../../components/admin/AdminTopBar'

const NAV_ITEMS = [
  { type: 'link', to: ADMIN_DASHBOARD_PATH, icon: LayoutDashboard, labelKey: 'admin.nav.dashboard', module: ADMIN_NAV_MODULES.dashboard, end: true },
  { type: 'link', to: adminPath('menus'), icon: Menu, labelKey: 'admin.nav.menus', module: ADMIN_NAV_MODULES.menus },
  { type: 'link', to: adminPath('pages'), icon: FileText, labelKey: 'admin.nav.pages', module: ADMIN_NAV_MODULES.pages },
  { type: 'link', to: adminPath('sliders'), icon: GalleryHorizontal, labelKey: 'admin.nav.heroSlider', module: ADMIN_NAV_MODULES.hero_slider },
  { type: 'link', to: adminPath('homepage'), icon: Home, labelKey: 'admin.nav.homepage', module: ADMIN_NAV_MODULES.homepage },
  { type: 'link', to: adminPath('news'), icon: Newspaper, labelKey: 'admin.nav.news', module: ADMIN_NAV_MODULES.news },
  { type: 'link', to: adminPath('events'), icon: Calendar, labelKey: 'admin.nav.events', module: ADMIN_NAV_MODULES.events },
  { type: 'group', groupKey: 'story_cards', icon: MessageSquareQuote, labelKey: 'admin.nav.storyCards', basePath: adminPath('story-cards') },
  { type: 'group', groupKey: 'forms', icon: ClipboardList, labelKey: 'admin.nav.forms', basePath: adminPath('forms') },
  { type: 'group', groupKey: 'recruitment', icon: Briefcase, labelKey: 'admin.nav.recruitment', basePath: adminPath('recruitment') },
  { type: 'link', to: adminPath('gallery'), icon: Image, labelKey: 'admin.nav.gallery', module: ADMIN_NAV_MODULES.gallery },
  { type: 'link', to: adminPath('ai-assistant'), icon: Bot, labelKey: 'admin.nav.aiAssistant', module: ADMIN_NAV_MODULES.ai_assistant },
  { type: 'link', to: adminPath('weekly-schedule'), icon: CalendarDays, labelKey: 'admin.nav.weeklySchedule', module: ADMIN_NAV_MODULES.weekly_schedule },
  { type: 'link', to: adminPath('settings'), icon: Settings, labelKey: 'admin.nav.settings', module: ADMIN_NAV_MODULES.settings },
  { type: 'link', to: adminPath('users'), icon: Users, labelKey: 'admin.nav.users', module: ADMIN_NAV_MODULES.users },
  { type: 'link', to: adminPath('activity-logs'), icon: History, labelKey: 'admin.nav.activityLogs', module: ADMIN_NAV_MODULES.activity_logs },
]

function hasNavAccess(item, hasModuleAccess) {
  if (item.modules?.length) {
    return item.modules.some((module) => hasModuleAccess(module))
  }
  if (item.module) {
    return hasModuleAccess(item.module)
  }
  return false
}

export default function AdminLayout() {
  const { t } = useTranslation()
  const { isAuthenticated, loading, logout, hasModuleAccess } = useAuth()
  const location = useLocation()
  const [openGroups, setOpenGroups] = useState(() => ({
    forms: location.pathname.startsWith(adminPath('forms')),
    story_cards: location.pathname.startsWith(adminPath('story-cards')),
    recruitment: location.pathname.startsWith(adminPath('recruitment')),
  }))

  useEffect(() => {
    setOpenGroups((prev) => ({
      ...prev,
      forms: prev.forms || location.pathname.startsWith(adminPath('forms')),
      story_cards: prev.story_cards || location.pathname.startsWith(adminPath('story-cards')),
      recruitment: prev.recruitment || location.pathname.startsWith(adminPath('recruitment')),
    }))
  }, [location.pathname])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated && !getAdminToken()) {
    return <Navigate to={ADMIN_LOGIN_PATH} state={{ from: location }} replace />
  }

  const visibleFormItems = ADMIN_FORM_MODULES.filter(({ key }) => hasModuleAccess(key))
  const visibleStoryCardItems = ADMIN_STORY_CARD_MODULES.filter(({ key }) => hasModuleAccess(key))
  const visibleRecruitmentItems = ADMIN_RECRUITMENT_MODULES.filter(({ key }) => hasModuleAccess(key))

  const getGroupItems = (groupKey) => {
    if (groupKey === 'story_cards') return visibleStoryCardItems
    if (groupKey === 'recruitment') return visibleRecruitmentItems
    return visibleFormItems
  }

  const renderNavItem = (item) => {
    if (item.type === 'group') {
      const groupItems = getGroupItems(item.groupKey)
      if (!groupItems.length) return null

      const isSection = location.pathname.startsWith(item.basePath)
      const isOpen = openGroups[item.groupKey]

      return (
        <div key={item.labelKey} className="space-y-1">
          <button
            type="button"
            onClick={() =>
              setOpenGroups((prev) => ({
                ...prev,
                [item.groupKey]: !prev[item.groupKey],
              }))
            }
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-start text-sm font-semibold transition-colors ${
              isSection ? 'text-primary' : 'text-text/70 hover:bg-primary/5 hover:text-primary'
            }`}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            <span className="flex-1">{t(item.labelKey)}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {isOpen ? (
            <div className="space-y-0.5 ps-9">
              {groupItems.map(({ route, labelKey }) => {
                const to = `${item.basePath}/${route}`
                const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`)
                return (
                  <Link
                    key={`${item.groupKey}-${route}`}
                    to={to}
                    className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-text/65 hover:bg-primary/5 hover:text-primary'
                    }`}
                  >
                    {t(labelKey)}
                  </Link>
                )
              })}
            </div>
          ) : null}
        </div>
      )
    }

    if (!hasNavAccess(item, hasModuleAccess)) return null

    const { to, icon: Icon, labelKey, end } = item
    const isActive = end
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`)

    return (
      <Link
        key={to}
        to={to}
        className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
          isActive ? 'bg-primary text-white' : 'text-text/70 hover:bg-primary/5 hover:text-primary'
        }`}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {t(labelKey)}
      </Link>
    )
  }

  return (
    <div className="admin-panel flex min-h-screen bg-background" dir="ltr" lang="en">
      <aside className="fixed inset-y-0 start-0 z-40 w-64 overflow-y-auto border-e border-border bg-white shadow-lg">
        <div className="flex h-16 items-center border-b border-border px-6">
          <Link to={ADMIN_DASHBOARD_PATH} className="block">
            <img
              src="/logo/logo-color.png"
              alt={t('brand.name')}
              className="h-10 w-auto object-contain"
            />
          </Link>
        </div>
        <nav className="space-y-1 p-4 pb-24">{NAV_ITEMS.map(renderNavItem)}</nav>
        <div className="fixed bottom-0 start-0 w-64 border-t border-border bg-white p-4">
          <button
            type="button"
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
          >
            <LogOut className="h-5 w-5" />
            {t('buttons.logout')}
          </button>
        </div>
      </aside>

      <main className="flex-1 ps-64">
        <AdminTopBar />
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
